import { CheckIcon } from '@heroicons/react/24/solid'

import { pv } from '../../motion'
import { COLORS } from '../../primitives'
import { Panel } from '../../v3/panels'
import { SPOTS } from '../../v4/world'
import { STAFF_BY_ID } from '../../v5/world'
import { Caption, Tag } from '../kit'
import { cam, cameraAt } from '../timeline'

/**
 * Extra „Teamfit“: Bevor Mara entscheidet, legt sie die Teamanalyse des
 * Customer-Success-Teams neben Lenas Profil. Lena ergänzt, was fehlt.
 */

const AXES = [
  'Emotionswahrnehmung',
  'Problemlösefähigkeit',
  'Gewissenhaftigkeit',
  'Extraversion',
  'Verträglichkeit',
  'Teamarbeit'
]
const TEAM = [0.86, 0.42, 0.38, 0.8, 0.86, 0.78]
const NEED = [0.8, 0.76, 0.74, 0.62, 0.78, 0.7]
const LENA = [0.9, 0.82, 0.84, 0.46, 0.8, 0.72]
const BOTH = TEAM.map((v, i) => Math.max(v, LENA[i]))
const GAPS = [1, 2]
const R = 100
const C = { x: 150, y: 140 }

const point = (i, v) => {
  const a = -Math.PI / 2 + (i * 2 * Math.PI) / AXES.length
  return [C.x + Math.cos(a) * R * v, C.y + Math.sin(a) * R * v]
}
const poly = (vals) => vals.map((v, i) => point(i, v).join(',')).join(' ')

const fade = (at, out) => ({
  ...pv('f', at),
  ...(out ? pv('g', out) : { '--g': 0 }),
  opacity: 'calc(var(--f) * (1 - var(--g)))'
})

export function Radar() {
  return (
    <div className='kx-radar'>
      <svg width='300' height='280' viewBox='0 0 300 280' aria-hidden='true'>
        {[0.25, 0.5, 0.75, 1].map((r) => (
          <polygon
            key={r}
            points={poly(AXES.map(() => r))}
            fill='none'
            stroke='#E3E8F0'
          />
        ))}
        {AXES.map((_, i) => {
          const [x, y] = point(i, 1)
          return (
            <line key={i} x1={C.x} y1={C.y} x2={x} y2={y} stroke='#E3E8F0' />
          )
        })}
        <polygon
          points={poly(TEAM)}
          fill='#DCE7FF'
          stroke={COLORS.blue}
          strokeWidth='2.5'
          style={fade([0.1, 0.18], [0.7, 0.78])}
        />
        <polygon
          points={poly(BOTH)}
          fill='#D7F8F1'
          stroke='#2CB89C'
          strokeWidth='2.5'
          style={fade([0.7, 0.78])}
        />
        <polygon
          points={poly(NEED)}
          fill='none'
          stroke={COLORS.ink}
          strokeWidth='2'
          strokeDasharray='5 5'
          style={fade([0.28, 0.34])}
        />
        <g
          style={{
            ...pv('h', [0.48, 0.58]),
            opacity: 'var(--h)',
            transformOrigin: `${C.x}px ${C.y}px`,
            transform: 'scale(calc(0.2 + 0.8 * var(--h)))'
          }}
        >
          <polygon
            points={poly(LENA)}
            fill='none'
            stroke={COLORS.pink}
            strokeWidth='3'
          />
        </g>
        {GAPS.map((i) => {
          const [x, y] = point(i, NEED[i])
          return (
            <circle
              key={i}
              cx={x}
              cy={y}
              r='8'
              fill={COLORS.pink}
              style={fade([0.32, 0.38], [0.66, 0.72])}
            />
          )
        })}
      </svg>
      {AXES.map((a, i) => {
        const [x, y] = point(i, 1.24)
        const gap = GAPS.includes(i)
        return (
          <span key={a} className='kx-axis' style={{ left: x, top: y }}>
            {gap && (
              <>
                <i
                  className='kx-axis__bg kx-axis__bg--gap'
                  style={fade([0.32, 0.38], [0.7, 0.76])}
                />
                <i
                  className='kx-axis__bg kx-axis__bg--done'
                  style={fade([0.74, 0.8])}
                />
              </>
            )}
            <span className='kx-axis__text'>
              {gap && (
                <b style={fade([0.74, 0.8])}>
                  <CheckIcon width={11} height={11} />
                </b>
              )}
              {a}
            </span>
          </span>
        )
      })}
    </div>
  )
}

export function TeamWorld() {
  const at = (id) => [STAFF_BY_ID[id].x, STAFF_BY_ID[id].y, 0]
  const lena = [SPOTS.front[0].x, SPOTS.front[0].y, 0]
  return (
    <div className='off-overlay'>
      <Tag
        at3={at('csLead')}
        dy={92}
        dx={60}
        show={[0.08, 0.14]}
        hide={[0.72, 0.76]}
        tone='blue'
      >
        Teamanalyse · Customer Success
      </Tag>
      <Tag
        at3={lena}
        dy={124}
        show={[0.46, 0.52]}
        hide={[0.92, 0.97]}
        tone='pink'
      >
        Mit Talent vergleichen
      </Tag>
      <Tag
        at3={at('csLead')}
        dy={92}
        dx={60}
        show={[0.76, 0.82]}
        hide={[0.92, 0.97]}
        tone='green'
      >
        <CheckIcon width={12} height={12} /> Lena ergänzt das Team
      </Tag>
    </div>
  )
}

export function TeamScreen() {
  return (
    <>
      <Panel at={[0.06, 0.14]} out={[0.92, 0.97]} className='off-doc kx-team'>
        <div className='off-doc__eyebrow'>Teamanalyse</div>
        <div className='off-doc__title'>Customer Success</div>
        <Radar />
        <div className='kx-legend'>
          <span>
            <i style={{ background: COLORS.blue }} /> Team
          </span>
          <span style={fade([0.28, 0.34])}>
            <i className='is-dash' /> Stellenanforderung
          </span>
          <span style={fade([0.48, 0.54])}>
            <i style={{ background: COLORS.pink }} /> Lena
          </span>
        </div>
      </Panel>

      <Caption
        show={[0.04, 0.1]}
        hide={[0.26, 0.3]}
        label='Teamfit'
        title='Passt Lena auch ins Team?'
        short='Die Teamanalyse zeigt, welche Stärken im Customer-Success-Team stecken.'
      >
        Passt Lena auch ins Team? Die Teamanalyse legt die Stärken des
        Customer-Success-Teams in ein Spinnennetzdiagramm.
      </Caption>
      <Caption
        show={[0.3, 0.36]}
        hide={[0.46, 0.5]}
        label='Teamfit'
        title='Was fehlt dem Team noch?'
        short='Die Stellenanforderung zeigt Lücken bei Problemlösefähigkeit und Gewissenhaftigkeit.'
      >
        Mit der Stellenanforderung daneben wird sichtbar, was dem Team noch
        fehlt. Hier Problemlösefähigkeit und Gewissenhaftigkeit.
      </Caption>
      <Caption
        show={[0.5, 0.56]}
        hide={[0.92, 0.97]}
        label='Teamfit'
        title='Lena ergänzt, statt zu kopieren.'
        short='Genau dort, wo das Team Luft nach oben hat, ist sie stark.'
      >
        Mit Talent vergleichen zeigt, dass Lena genau dort stark ist. Sie
        ergänzt das Team, statt es zu kopieren.
      </Caption>
    </>
  )
}

export const TEAM_EXTRA = {
  id: 'team',
  after: 'report',
  len: 340,
  label: 'Teamfit',
  title: 'Passt Lena ins Team?',
  teaser:
    'Die Teamanalyse zeigt, welche Stärken dem Customer-Success-Team noch fehlen und wo Lena sie ergänzt.',
  camera: (p, base) =>
    cameraAt(
      [
        { p: 0, cam: base },
        { p: 0.08, cam: cam([15, 13, 1], 1.05, -0.22) },
        { p: 0.92, cam: cam([15, 13, 1], 1.05, -0.22) },
        { p: 1, cam: base }
      ],
      p
    ),
  World: TeamWorld,
  Screen: TeamScreen
}
